import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import {
  CropperPosition,
  ImageCroppedEvent,
  ImageCropperComponent,
  LoadedImage,
} from 'ngx-image-cropper';

import { NotificationService } from '@blaze-shared/platform-data-access';

import { getAspectRatio, getAspectRatioValue } from '../../util/public-api';

@Component({
  selector: 'blaze-uploader-image-edit',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-edit.component.html',
  styleUrls: ['./image-edit.component.scss'],
  imports: [ImageCropperComponent, MatIcon, FormsModule],
})
export class ImageEditComponent {
  private readonly notificationService = inject(NotificationService);

  readonly fileToEdit = input.required<File | Blob>();
  readonly cropForceAspectRatio = input<{ width: number; height: number }>();
  readonly cropMaximumImageSize = input<{ width?: number; height?: number }>();
  readonly imageCropped = output<ImageCroppedEvent>();

  private readonly loadedImage = signal<LoadedImage | undefined>(undefined);

  protected readonly requestedRatioWidth = signal<number | null>(null);
  protected readonly requestedRatioHeight = signal<number | null>(null);

  protected readonly ratioWidth = computed(
    () => this.requestedRatioWidth() ?? this.cropForceAspectRatio()?.width ?? null,
  );
  protected readonly ratioHeight = computed(
    () => this.requestedRatioHeight() ?? this.cropForceAspectRatio()?.height ?? null,
  );

  protected readonly originalFile = computed<File>(() => {
    const file = this.fileToEdit();

    if (file instanceof File === false) {
      throw new TypeError('File for editing does not have a valid file associated.');
    }

    return file;
  });

  protected readonly outputFormat = computed(() => {
    const file = this.originalFile();

    if (file.type === 'image/png') {
      return 'png';
    }
    if (file.type === 'image/webp') {
      return 'webp';
    }
    return 'jpeg';
  });

  protected readonly aspectRatio = computed(() =>
    getAspectRatioValue(
      getAspectRatio(this.cropForceAspectRatio(), this.ratioWidth(), this.ratioHeight()),
    ),
  );
  protected readonly isAspectRatioLocked = computed<boolean>(() => {
    const cropAspectRatio = this.cropForceAspectRatio();
    const ratioWidth = this.ratioWidth();
    const ratioHeight = this.ratioHeight();
    return cropAspectRatio !== undefined || !!(ratioWidth && ratioHeight);
  });

  protected readonly resizeToSize = computed<{ width?: number; height?: number } | undefined>(() =>
    calculateMaxSize(this.cropMaximumImageSize(), this.loadedImage()),
  );
  protected readonly canInputRatio = computed(() => !this.cropForceAspectRatio());
  protected readonly showCropper = signal<boolean>(false);
  protected readonly isProcessing = signal<boolean>(false);
  protected readonly canvasRotation = signal<number>(0);

  protected cropperPosition: CropperPosition | undefined = undefined;

  protected onCropperReady(): void {
    this.isProcessing.set(false);
  }

  protected onImageLoaded(event: LoadedImage): void {
    this.loadedImage.set(event);
    this.showCropper.set(true);
  }

  protected onImageLoadFailed(): void {
    this.showCropper.set(false);
    this.notificationService.error('An unknown error occurred while loading the image.');
  }

  protected onImageCropped(event: ImageCroppedEvent): void {
    this.imageCropped.emit(event);
  }

  protected onRatioChanged(
    width: number | undefined | null,
    height: number | undefined | null,
  ): void {
    if (width !== undefined) {
      this.requestedRatioWidth.set(width);
    }
    if (height !== undefined) {
      this.requestedRatioHeight.set(height);
    }
  }

  protected onRotateImage(cw: boolean): void {
    this.isProcessing.set(true);
    setTimeout(() => {
      let updated = this.canvasRotation() + (cw ? 1 : -1);
      if (updated < 0) {
        updated = 3;
      } else if (updated > 3) {
        updated = 0;
      }
      this.canvasRotation.set(updated);
    });
  }
}

function calculateMaxSize(
  maxSize: { width?: number; height?: number } | undefined,
  image: LoadedImage | undefined,
): { width?: number; height?: number } | undefined {
  if (!image || !maxSize) {
    return undefined;
  }

  const imageSize = image.original.size;
  const { width: maxW, height: maxH } = maxSize;
  let result: { width?: number; height?: number } = {};
  if (maxW && imageSize.width > maxW) {
    result = { width: maxW };
  }
  if (maxH && imageSize.height > maxH) {
    result = { ...result, height: maxH };
  }
  return result;
}
